import * as React from "react";
import { addMinutes, endOfDay, format, isSameDay, isBefore, startOfDay } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";

export interface ScheduleDateTimePickerProps {
  /** The chosen moment; date and time together. */
  value: Date | undefined;
  onChange: (value: Date) => void;
  /** The earliest day that can be picked; defaults to today. */
  minDate?: Date;
  maxDate?: Date;
  /** Times are offered in steps of this many minutes. */
  stepMinutes?: number;
  /** A time must be at least this far in the future. */
  minLeadMinutes?: number;
  className?: string;
}

function timesFor(day: Date, stepMinutes: number): Date[] {
  const times: Date[] = [];
  for (let t = startOfDay(day); isBefore(t, endOfDay(day)); t = addMinutes(t, stepMinutes)) times.push(t);
  return times;
}

function withTimeOf(day: Date, time: Date): Date {
  const next = new Date(day);
  next.setHours(time.getHours(), time.getMinutes(), 0, 0);
  return next;
}

/**
 * A calendar beside a scrollable list of times, for scheduling a post. Times too soon to post are
 * disabled, and picking a day keeps the chosen time when it's still possible that day.
 */
export const ScheduleDateTimePicker: React.FC<ScheduleDateTimePickerProps> = ({
  value,
  onChange,
  minDate,
  maxDate,
  stepMinutes = 30,
  minLeadMinutes = 5,
  className,
}) => {
  const earliest = addMinutes(new Date(), minLeadMinutes);
  const firstDay = startOfDay(minDate && isBefore(earliest, minDate) ? minDate : earliest);
  const day = value ?? earliest;
  const times = timesFor(day, stepMinutes);
  const tooSoon = (time: Date) => isBefore(time, earliest);

  const selectedRef = React.useRef<HTMLButtonElement>(null);
  React.useEffect(() => {
    selectedRef.current?.scrollIntoView?.({ block: "center" });
  }, [day.toDateString()]);

  const pickDay = (picked: Date | undefined) => {
    if (!picked) return;
    const sameTime = withTimeOf(picked, value ?? earliest);
    if (!tooSoon(sameTime)) {
      onChange(sameTime);
      return;
    }
    const first = timesFor(picked, stepMinutes).find(time => !tooSoon(time));
    if (first) onChange(first);
  };

  return (
    <div className={cn("flex flex-col gap-3 sm:flex-row", className)} data-testid="schedule-date-time-picker">
      <Calendar
        // Day numbers a step smaller than the kit's default, to sit lighter beside the time list.
        className="[&_[data-day]]:text-xs"
        mode="single"
        selected={value}
        onSelect={pickDay}
        defaultMonth={day}
        disabled={[{ before: firstDay }, ...(maxDate ? [{ after: maxDate }] : [])]}
        startMonth={firstDay}
        endMonth={maxDate}
      />
      <div className="flex flex-col gap-1 sm:w-32">
        <span className="px-1 text-xs font-bold uppercase tracking-wider text-slate-400">Time</span>
        <ul className="flex max-h-64 flex-col gap-0.5 overflow-y-auto pr-1" aria-label="Times">
          {times.map(time => {
            const selected = !!value && isSameDay(value, time) && format(value, "HH:mm") === format(time, "HH:mm");
            return (
              <li key={time.getTime()}>
                <button
                  ref={selected ? selectedRef : undefined}
                  type="button"
                  disabled={tooSoon(time)}
                  aria-pressed={selected}
                  onClick={() => onChange(time)}
                  className={cn(
                    "w-full rounded-md px-2 py-1.5 text-left text-sm cursor-pointer disabled:cursor-not-allowed disabled:text-slate-300",
                    selected ? "bg-primary text-white" : "text-slate-700 hover:bg-slate-100"
                  )}
                >
                  {format(time, "h:mm a")}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};
